import React, { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { getSoilAIFlyovers, getSoilAIVisualisation } from "services";

import { generateRandomId, useCancelAsyncOnUnmount } from "utils";

import { DEFAULT_CACHE_TIME, GET_FLYOVERS } from "consts";

import { Card, Col, Form, MapInteractiveWithBoundedOverlay, Option, Row, Select, SkeletonWrapper } from "components";

const layers = [
  {
    layer: "NDVI",
    description: "Vegetation density",
  },
  {
    layer: "NDVI-MAX",
    description: "Vegetation density, maxed over 90 days",
  },
  {
    layer: "NDVI-COLOURISED",
    description: "Vegetation density, colourised",
  },
  {
    layer: "NDRE",
    description: "Vegetation density",
  },
  {
    layer: "LAI",
    description: "Vegetation density",
  },
  {
    layer: "EVI",
    description: "Vegetation density",
  },
  {
    layer: "MOISTURE-INDEX",
    description: "Moisture",
  },
  {
    layer: "NATURAL-COLOR",
    description: "Natural colour",
  },
  {
    layer: "FALSE-COLOR",
    description: "False colour vegetation",
  },
  {
    layer: "AGRICULTURE",
    description: "Agricultural land",
  },
  {
    layer: "AGRICULTURE-FILTERED",
    description: "Agricultural land",
  },
];

export const CropMap = ({ field = null, geometry, location, defaultZoom, selectedDate, timeSpan }) => {
  const [form] = Form.useForm();
  const [overlay, setOverlay] = useState(null);
  const [currentMap, setCurrentMap] = useState(null);
  const [date, setDate] = useState(null);
  const [layer, setLayer] = useState(null);

  const signal = useCancelAsyncOnUnmount()();

  const loadCropVis = useCallback(
    ({ bbox, size, date, layer }) => {
      getSoilAIVisualisation({
        bbox,
        size,
        date,
        layer,
        signal,
      }).then((res) => {
        setOverlay({
          img: res,
          bbox,
        });
      });
    },
    [signal]
  );

  const { data: flyovers, isLoading: isLoadingFlyovers } = useQuery({
    queryKey: [GET_FLYOVERS, currentMap?.bbox, timeSpan],
    queryFn: () => getSoilAIFlyovers({ bbox: currentMap?.bbox, timeSpan }),
    enabled: !!currentMap,
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_CACHE_TIME,
  });

  const getMapData = (data) => {
    // get size and bounding box from map change handler
    const { size } = data;
    const bbox = [data.marginBounds.sw.lng, data.marginBounds.sw.lat, data.marginBounds.ne.lng, data.marginBounds.ne.lat];

    setCurrentMap({ bbox, size });

    // only get a new overlay if something changed
    if (!overlay || (overlay.bbox.north !== bbox[3] && overlay.bbox.west !== bbox[0])) {
      setCurrentMap({ bbox, size });
    }
  };

  useEffect(() => {
    if (currentMap && date && layer) {
      loadCropVis({ bbox: currentMap.bbox, size: currentMap.size, date, layer });
    }
  }, [currentMap, date, layer, loadCropVis]);

  useEffect(() => {
    setDate(null);
  }, [field, location, geometry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if the flyovers include the selected dates (date is formatted YYYY-MM-DD, selected date is iso)
  const selectedFlyover = selectedDate ? flyovers?.find(({ date }) => selectedDate.includes(date)) : undefined;

  useEffect(() => {
    if (!selectedFlyover) {
      return;
    }

    // We need to set the date here explicitly as well because setFieldValue doesn't cut it
    setDate(selectedFlyover.date);
    form.setFieldValue("flyover", selectedFlyover.date);
  }, [form, selectedFlyover]);

  if (!isLoadingFlyovers && flyovers?.length === 0) {
    return <p>Grok deactivated the satellites. Come back later.</p>;
  }

  return (
    <Form name={generateRandomId()} form={form}>
      <Row>
        <Col sm={24} md={12}>
          <Select
            name="layer"
            label="Select satellite layer"
            rules={[{ required: true }]}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            onChange={(v) => setLayer(v)}
            optionLabelProp={"label"}
          >
            {layers.map((l, i) => (
              <Option value={l.layer} key={i} label={l.layer}>
                {l.layer}
                <br />
                <small>{l.description}</small>
              </Option>
            ))}
          </Select>
        </Col>

        <Col sm={24} md={12}>
          <SkeletonWrapper isLoading={isLoadingFlyovers} type="block" height="4rem">
            <Select
              name="flyover"
              label="Select flyover date"
              rules={[{ required: true }]}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              onChange={(v) => setDate(v)}
              optionLabelProp={"label"}
              // is undefined if there is none selected, this is only actually needed to make the label go up as if there is something selected
              // We needed to use setFieldValue instead of just this, otherwise the value would not be overriden
              value={selectedFlyover?.date}
            >
              {flyovers?.map((f, i) => (
                <Option value={f.date} key={i} label={f.date}>
                  {f.date}
                  <br />
                  <small>{f.clouds}% cloudcover</small>
                </Option>
              ))}
            </Select>
          </SkeletonWrapper>
        </Col>
      </Row>

      <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]} className="margin-top-100">
        <Col md={24}>
          {location && geometry && (
            <Card className="map-card">
              <MapInteractiveWithBoundedOverlay
                geometry={geometry}
                location={location}
                onChange={(v) => getMapData(v)}
                overlay={overlay}
                field={field}
                defaultZoom={defaultZoom}
                // disableDrag={true}
                // scaleControl={false}
                // zoomControl={false}
              />
            </Card>
          )}
        </Col>
      </Row>
    </Form>
  );
};
